import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SportsIcon from "@mui/icons-material/Sports";
import dayjs from "dayjs";
import { formatLeagueDateTime, resolveLeagueTimezone } from "utils/league-datetime";
import { Link as RouterLink } from "react-router-dom";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import UserContext from "context/UserContext";
import EmptyPage from "components/common/EmptyPage";
import performRequest from "network/perform-request";
import { downloadAuthenticatedFile } from "network/download-authenticated-file";
import {
  leagueCustomerMatchesRoute,
  leagueCustomerRefereesRoute,
  leagueCustomerTournamentsRoute,
  leagueCustomerVenuesRoute,
  leagueTournamentFieldsRoute,
  leagueTournamentGroupsRoute,
  leagueTournamentMatchdaysRoute,
  leagueTournamentMatchPdfRoute,
  leagueTournamentMatchRoute,
} from "network/api-routes";
import { ROUTES } from "constants/routes";
import { GESTION_ROUTES } from "constants/gestion-routes";
import { useGestionCustomer } from "hooks/useGestionCustomer";
import { parseLeaguePaginatedResponse } from "components/pages/Gestion/gestion-pagination";
import GestionListPagination from "components/pages/Gestion/GestionListPagination";
import GestionTable from "components/pages/Gestion/GestionTable";
import GestionPageHeader from "components/pages/Gestion/GestionPageHeader";
import {
  fieldOptionLabel,
  matchFieldName,
  withCurrentOption,
} from "components/pages/Tournament/League/league-match-fields";

const teamName = (lineup) => lineup?.team?.name || lineup?.team?.short_name || "—";

const STATUS_FILTER_OPTIONS = ["pending", "finished"];
const STATUS_LABEL_IDS = { pending: "league.matchState.pending", finished: "league.matchState.closed" };

const GestionMatches = () => {
  const { customerId, customerName } = useGestionCustomer();
  const { user } = useContext(UserContext);
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const [matches, setMatches] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [referees, setReferees] = useState([]);
  const [groups, setGroups] = useState([]);
  const [matchdays, setMatchdays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tournamentFilter, setTournamentFilter] = useState(null);
  const [groupFilter, setGroupFilter] = useState(null);
  const [matchdayFilter, setMatchdayFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [fieldFilter, setFieldFilter] = useState(null);
  const [fieldFilterOptions, setFieldFilterOptions] = useState([]);
  const [refereeFilter, setRefereeFilter] = useState(null);
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [refereeDialog, setRefereeDialog] = useState(null);
  const [selectedReferee, setSelectedReferee] = useState(null);
  const [fieldOptions, setFieldOptions] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [savingReferee, setSavingReferee] = useState(false);

  const handleError = useCallback(
    (error) => {
      enqueueSnackbar(error?.response?.data?.message || intl.formatMessage({ id: "gestion.error.generic" }), {
        variant: "error",
      });
    },
    [enqueueSnackbar, intl],
  );

  const loadMatches = useCallback(() => {
    if (!customerId || !user) return;
    setLoading(true);
    performRequest(
      "GET",
      leagueCustomerMatchesRoute(customerId),
      {
        page: page + 1,
        page_size: pageSize,
        name: search.trim() || undefined,
        tournament_id: tournamentFilter?.revaId || tournamentFilter?.id || undefined,
        tournament_group_id: groupFilter?.id || undefined,
        matchday_id: matchdayFilter?.id || undefined,
        status: statusFilter || undefined,
        tournament_field_id: fieldFilter?.id || undefined,
        referee_id: refereeFilter?.id || undefined,
        datetime_start: dateStart ? dayjs(dateStart).format("YYYY-MM-DD") : undefined,
        datetime_end: dateEnd ? dayjs(dateEnd).format("YYYY-MM-DD") : undefined,
      },
      true,
      user,
    )
      .then((response) => {
        const parsed = parseLeaguePaginatedResponse(response.data);
        setMatches(parsed.results);
        setPage(parsed.page);
        setTotalPages(parsed.totalPages);
        setTotalResults(parsed.totalResults);
      })
      .catch((error) => {
        handleError(error);
        setMatches([]);
      })
      .finally(() => setLoading(false));
  }, [
    customerId,
    dateEnd,
    dateStart,
    fieldFilter,
    groupFilter,
    handleError,
    matchdayFilter,
    page,
    pageSize,
    refereeFilter,
    search,
    statusFilter,
    tournamentFilter,
    user,
  ]);

  useEffect(() => {
    if (!customerId || !user) return;
    performRequest("GET", leagueCustomerTournamentsRoute(customerId), { page_size: 100 }, true, user)
      .then((response) => setTournaments(response.data?.results ?? []))
      .catch(() => setTournaments([]));
    performRequest("GET", leagueCustomerRefereesRoute(customerId), {}, true, user)
      .then((response) => setReferees(Array.isArray(response.data) ? response.data.filter((r) => !r.archived) : []))
      .catch(() => setReferees([]));
    performRequest("GET", leagueCustomerVenuesRoute(customerId), {}, true, user)
      .then((response) => {
        const venues = Array.isArray(response.data) ? response.data : [];
        setFieldFilterOptions(
          venues.flatMap((venue) =>
            (venue.fields || venue.spaces || [])
              .filter((field) => !field.archived)
              .map((field) => ({
                id: field.tournament_field_id || field.id,
                name: field.name,
                display_name: field.display_name || `${venue.name} — ${field.name}`,
              })),
          ),
        );
      })
      .catch(() => setFieldFilterOptions([]));
  }, [customerId, user]);

  useEffect(() => {
    setGroupFilter(null);
    setMatchdayFilter(null);
    setGroups([]);
    setMatchdays([]);
    if (!tournamentFilter || !user) return;
    const tid = tournamentFilter.revaId || tournamentFilter.id;
    performRequest("GET", leagueTournamentGroupsRoute(tid), {}, true, user)
      .then((res) => setGroups(Array.isArray(res.data) ? res.data : res.data?.results ?? []))
      .catch(() => setGroups([]));
    performRequest("GET", leagueTournamentMatchdaysRoute(tid), {}, true, user)
      .then((res) => setMatchdays(Array.isArray(res.data) ? res.data : res.data?.results ?? []))
      .catch(() => setMatchdays([]));
  }, [tournamentFilter, user]);

  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  const tournamentApiId = (match) => match?.tournament_id || match?.tournament?.id;

  const openPdf = (match) => {
    const tournamentId = tournamentApiId(match);
    if (!tournamentId) return;
    downloadAuthenticatedFile({
      url: leagueTournamentMatchPdfRoute(tournamentId, match.id),
      user,
      filename: `match-${match.id}.pdf`,
      accept: "application/pdf",
    }).catch(handleError);
  };

  const openRefereeDialog = (match) => {
    const currentField = match.tournament_field || match.tournamentField;
    setRefereeDialog(match);
    setSelectedReferee(
      referees.find((r) => r.id === match.referee_id) ||
        (match.referee_id ? { id: match.referee_id, name: match.referee?.name || `#${match.referee_id}` } : null),
    );
    setSelectedField(match.tournament_field_id ? { id: match.tournament_field_id, name: currentField?.name } : null);
    setFieldOptions([]);
    const tournamentId = tournamentApiId(match);
    if (!tournamentId) return;
    performRequest("GET", leagueTournamentFieldsRoute(tournamentId), {}, true, user)
      .then((response) => {
        const options = Array.isArray(response.data) ? response.data : [];
        setFieldOptions(withCurrentOption(options, match.tournament_field_id, currentField));
        setSelectedField((prev) => options.find((option) => String(option.id) === String(prev?.id)) || prev);
      })
      .catch(() => setFieldOptions(withCurrentOption([], match.tournament_field_id, currentField)));
  };

  const saveReferee = () => {
    const tournamentId = tournamentApiId(refereeDialog);
    if (!tournamentId) return;
    // Only changed values are sent: resending an unchanged cancha re-checks its venue attachment.
    const payload = {};
    const refereeId = selectedReferee?.id ?? null;
    if (String(refereeId ?? "") !== String(refereeDialog.referee_id ?? "")) {
      payload.referee_id = refereeId;
    }
    const fieldId = selectedField?.id ?? null;
    if (String(fieldId ?? "") !== String(refereeDialog.tournament_field_id ?? "")) {
      payload.tournament_field_id = fieldId;
    }
    setSavingReferee(true);
    performRequest("PATCH", leagueTournamentMatchRoute(tournamentId, refereeDialog.id), payload, true, user)
      .then(() => {
        enqueueSnackbar(intl.formatMessage({ id: "gestion.matches.matchUpdated" }), { variant: "success" });
        setRefereeDialog(null);
        loadMatches();
      })
      .catch(handleError)
      .finally(() => setSavingReferee(false));
  };

  const planilleroLink = (match) => {
    const tournamentKey = match.tournament?.uuid_ || match.tournament?.uuid;
    if (!tournamentKey) return null;
    return {
      pathname: `${ROUTES.PUBLIC.TOURNAMENT}/${tournamentKey}/planillero/${match.id}`,
      state: {
        gestionCustomerId: customerId,
        gestionReturnPath: GESTION_ROUTES.matches(customerId),
      },
    };
  };

  const showGroupColumn =
    Boolean(tournamentFilter && groups.length > 0) ||
    matches.some((m) => m.tournament_group || m.tournamentGroup || m.tournament_group_id);

  if (!customerId) return null;

  return (
    <Box sx={{ width: "100%", maxWidth: "lg", mx: "auto", p: { xs: 3, md: 4 } }}>
      <Stack spacing={3}>
        <GestionPageHeader
          title={intl.formatMessage({ id: "gestion.matches.title" }, { name: customerName || "" })}
          subtitle={intl.formatMessage({ id: "gestion.matches.subtitle" })}
        />

        <Stack direction={{ xs: "column", md: "row" }} spacing={2} useFlexGap flexWrap="wrap">
        <TextField
          fullWidth
          size="small"
          label={intl.formatMessage({ id: "gestion.matches.search" })}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 180, flex: 1 }}
        />
        <Autocomplete
          size="small"
          sx={{ minWidth: 220 }}
          options={tournaments}
          getOptionLabel={(option) => option.name || `#${option.revaId || option.id}`}
          value={tournamentFilter}
          onChange={(_, value) => {
            setTournamentFilter(value);
            setPage(0);
          }}
          renderInput={(params) => (
            <TextField {...params} label={intl.formatMessage({ id: "gestion.matches.tournamentFilter" })} />
          )}
        />
        <Autocomplete
          size="small"
          sx={{ minWidth: 180 }}
          options={groups}
          getOptionLabel={(option) => option.name || `#${option.id}`}
          value={groupFilter}
          onChange={(_, value) => {
            setGroupFilter(value);
            setPage(0);
          }}
          disabled={!tournamentFilter}
          renderInput={(params) => (
            <TextField {...params} label={intl.formatMessage({ id: "gestion.matches.filterGroup" })} />
          )}
        />
        <Autocomplete
          size="small"
          sx={{ minWidth: 180 }}
          options={matchdays}
          getOptionLabel={(option) => option.name || `#${option.id}`}
          value={matchdayFilter}
          onChange={(_, value) => {
            setMatchdayFilter(value);
            setPage(0);
          }}
          disabled={!tournamentFilter}
          renderInput={(params) => (
            <TextField {...params} label={intl.formatMessage({ id: "gestion.matches.filterMatchday" })} />
          )}
        />
        <Autocomplete
          size="small"
          sx={{ minWidth: 180 }}
          options={STATUS_FILTER_OPTIONS}
          getOptionLabel={(option) => intl.formatMessage({ id: STATUS_LABEL_IDS[option] })}
          value={statusFilter}
          onChange={(_, value) => {
            setStatusFilter(value);
            setPage(0);
          }}
          renderInput={(params) => (
            <TextField {...params} label={intl.formatMessage({ id: "gestion.matches.status" })} />
          )}
        />
        <Autocomplete
          size="small"
          sx={{ minWidth: 180 }}
          options={fieldFilterOptions}
          getOptionLabel={fieldOptionLabel}
          isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
          value={fieldFilter}
          onChange={(_, value) => {
            setFieldFilter(value);
            setPage(0);
          }}
          renderInput={(params) => (
            <TextField {...params} label={intl.formatMessage({ id: "gestion.matches.field" })} />
          )}
        />
        <Autocomplete
          size="small"
          sx={{ minWidth: 180 }}
          options={referees}
          getOptionLabel={(option) => option.name || ""}
          isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
          value={refereeFilter}
          onChange={(_, value) => {
            setRefereeFilter(value);
            setPage(0);
          }}
          renderInput={(params) => (
            <TextField {...params} label={intl.formatMessage({ id: "gestion.matches.referee" })} />
          )}
        />
        <TextField
          size="small"
          type="date"
          label={intl.formatMessage({ id: "gestion.matches.dateFrom" })}
          InputLabelProps={{ shrink: true }}
          value={dateStart}
          onChange={(event) => {
            setDateStart(event.target.value);
            setPage(0);
          }}
        />
        <TextField
          size="small"
          type="date"
          label={intl.formatMessage({ id: "gestion.matches.dateTo" })}
          InputLabelProps={{ shrink: true }}
          value={dateEnd}
          onChange={(event) => {
            setDateEnd(event.target.value);
            setPage(0);
          }}
        />
        <Button variant="outlined" color="primary" size="small" onClick={loadMatches}>
          {intl.formatMessage({ id: "gestion.matches.applyFilters" })}
        </Button>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Box>
      ) : matches.length === 0 ? (
        <EmptyPage text={intl.formatMessage({ id: "gestion.matches.empty" })} />
      ) : (
        <>
          <GestionTable
            headers={[
              { key: "matchday", title: intl.formatMessage({ id: "league.matches.matchday" }) },
              { key: "tournament", title: intl.formatMessage({ id: "gestion.matches.tournamentFilter" }) },
              { key: "datetime", title: intl.formatMessage({ id: "league.matches.datetime" }) },
              ...(showGroupColumn
                ? [{ key: "group", title: intl.formatMessage({ id: "gestion.matches.group" }) }]
                : []),
              { key: "local", title: intl.formatMessage({ id: "league.matches.local" }) },
              { key: "visiting", title: intl.formatMessage({ id: "league.matches.visiting" }) },
              { key: "field", title: intl.formatMessage({ id: "gestion.matches.field" }) },
              { key: "referee", title: intl.formatMessage({ id: "gestion.matches.referee" }) },
              { key: "status", title: intl.formatMessage({ id: "gestion.matches.status" }) },
              {
                key: "actions",
                title: intl.formatMessage({ id: "gestion.actions" }),
                align: "right",
                renderCell: (_value, match) => {
                  const url = planilleroLink(match);
                  return (
                    <>
                      {url && (
                        <Tooltip title={intl.formatMessage({ id: "league.planillero.open" })}>
                          <IconButton size="small" component={RouterLink} to={url}>
                            <AssignmentIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={intl.formatMessage({ id: "league.matches.pdf" })}>
                        <IconButton size="small" onClick={() => openPdf(match)}>
                          <PictureAsPdfIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={intl.formatMessage({ id: "gestion.matches.assignRefereeAndField" })}>
                        <IconButton size="small" onClick={() => openRefereeDialog(match)}>
                          <SportsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  );
                },
              },
            ]}
            rows={matches.map((match) => {
              const local = match.local_lineup || match.localLineup;
              const visiting = match.visiting_lineup || match.visitingLineup;
              const scheduled = match.scheduled_time || match.scheduledTime;
              return {
                ...match,
                matchday: match.matchday?.name || "—",
                datetime: scheduled ? formatLeagueDateTime(scheduled, resolveLeagueTimezone(match.tournament)) : "—",
                group: match.tournament_group?.name || match.tournamentGroup?.name || "—",
                local: teamName(local),
                visiting: teamName(visiting),
                field: matchFieldName(match) || "—",
                referee: match.referee?.name || "—",
                tournament: match.tournament?.name || "—",
                status: intl.formatMessage({
                  id: match.is_finished ? STATUS_LABEL_IDS.finished : STATUS_LABEL_IDS.pending,
                }),
              };
            })}
          />
          <GestionListPagination
            page={page}
            pageSize={pageSize}
            totalPages={totalPages}
            totalResults={totalResults}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
          />
        </>
      )}
      </Stack>

      <Dialog open={Boolean(refereeDialog)} onClose={() => setRefereeDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{intl.formatMessage({ id: "gestion.matches.assignRefereeAndField" })}</DialogTitle>
        <DialogContent>
          <Autocomplete
            sx={{ mt: 1 }}
            options={withCurrentOption(referees, refereeDialog?.referee_id, refereeDialog?.referee)}
            getOptionLabel={(option) => option.name || ""}
            isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
            value={selectedReferee}
            onChange={(_, value) => setSelectedReferee(value)}
            renderInput={(params) => (
              <TextField {...params} label={intl.formatMessage({ id: "gestion.matches.referee" })} />
            )}
          />
          <Autocomplete
            sx={{ mt: 2 }}
            options={fieldOptions}
            getOptionLabel={fieldOptionLabel}
            isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
            value={selectedField}
            onChange={(_, value) => setSelectedField(value)}
            renderInput={(params) => (
              <TextField {...params} label={intl.formatMessage({ id: "gestion.matches.field" })} />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="primary" onClick={() => setRefereeDialog(null)}>
            {intl.formatMessage({ id: "league.planillero.cancel" })}
          </Button>
          <Button variant="contained" color="primary" onClick={saveReferee} disabled={savingReferee}>
            {intl.formatMessage({ id: "league.planillero.save" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GestionMatches;
